/**
 * PacerTaskDialog — Detail dialog for either:
 *   • a Pacer Tree milestone (rolls up many tasks), or
 *   • a single department-level Pacer task.
 *
 * For an admin/engineer user it also surfaces:
 *   • Multi-assignee picker (existing user emails)
 *   • Per-assignee WhatsApp number opt-in (E.164)
 *   • Inline date / status edit
 *   • "Send reminder now" button (manual fire)
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Calendar, X, Mail, MessageCircle, Bell } from 'lucide-react';
import PacerTaskCollab from './PacerTaskCollab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function getCurrentUser() {
  try {
    return {
      role: localStorage.getItem('user_role') || '',
      email: localStorage.getItem('user_email') || '',
    };
  } catch { return {}; }
}

function getToken() {
  return localStorage.getItem('admin_token') || '';
}

function StatusPill({ status }) {
  const s = (status || '').toLowerCase();
  let bg = '#E5E7EB', fg = '#374151', label = status || 'Not started';
  if (s.includes('complete')) { bg = '#DCFCE7'; fg = '#15803D'; label = 'Completed'; }
  else if (s.includes('progress')) { bg = '#FEF3C7'; fg = '#92400E'; label = 'In progress'; }
  else if (s.includes('cancel') || s.includes('block')) { bg = '#FEE2E2'; fg = '#B91C1C'; label = 'Blocked'; }
  else if (s.includes('hold')) { bg = '#E0E7FF'; fg = '#4338CA'; label = 'On hold'; }
  return (
    <span
      className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase rounded-md"
      style={{ backgroundColor: bg, color: fg, fontFamily: 'Manrope' }}
    >
      {label}
    </span>
  );
}

function AdminTaskEditor({ task, onUpdated }) {
  const [allUsers, setAllUsers] = useState([]);
  const [assignees, setAssignees] = useState(task.assignees || []);
  const [whatsappMap, setWhatsappMap] = useState(task.whatsapp_numbers || {});
  const [picker, setPicker] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(({ data }) => setAllUsers(data || []))
      .catch(() => setAllUsers([]));
  }, []);

  const addAssignee = (email) => {
    email = (email || '').toLowerCase().trim();
    if (!email || assignees.includes(email)) return;
    setAssignees((a) => [...a, email]);
    setPicker('');
  };
  const removeAssignee = (email) => setAssignees((a) => a.filter((x) => x !== email));
  const setWa = (email, num) => setWhatsappMap((m) => ({ ...m, [email]: num }));

  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(
        `${API}/admin/pacer/tasks/${task.id}`,
        { assignees, whatsapp_numbers: whatsappMap },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Assignees saved');
      onUpdated?.({ ...task, assignees, whatsapp_numbers: whatsappMap });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const fireReminder = async () => {
    if (!assignees.length) {
      toast.error('Add at least one assignee first');
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/pacer/tasks/${task.id}/send-reminder`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if ((data.sent || 0) === 0) {
        toast.warning(`Reminder not sent — ${data.reason || 'no recipients'}`);
      } else {
        toast.success(`Reminder sent to ${data.sent} recipient(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reminder failed');
    }
  };

  const userOptions = allUsers.filter((u) => u.email && !assignees.includes(u.email.toLowerCase()));

  return (
    <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E5E7EB' }}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
          Admin · Assignees & alerts
        </h4>
        <button
          type="button"
          onClick={fireReminder}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold"
          style={{ backgroundColor: '#1F4FBE', color: 'white', fontFamily: 'Manrope' }}
          data-testid="pacer-task-send-reminder-btn"
        >
          <Bell className="w-3 h-3" />
          Send reminder now
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-2">
        {assignees.length === 0 && (
          <span className="text-[11px] italic" style={{ color: '#9CA3AF', fontFamily: 'Manrope' }}>
            No assignees yet
          </span>
        )}
        {assignees.map((e) => (
          <span
            key={e}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px]"
            style={{ backgroundColor: '#EEF2FF', color: '#3730A3', fontFamily: 'Manrope' }}
          >
            {e}
            <button type="button" onClick={() => removeAssignee(e)} aria-label={`Remove ${e}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>

      <select
        value={picker}
        onChange={(ev) => addAssignee(ev.target.value)}
        className="w-full text-[12px] rounded-md border px-2 py-1.5 mb-3"
        style={{ borderColor: '#D1D5DB', fontFamily: 'Manrope' }}
        data-testid="pacer-task-assignee-picker"
      >
        <option value="">+ Add an assignee…</option>
        {userOptions.map((u) => (
          <option key={u.id || u.email} value={u.email}>
            {u.name || u.email} ({u.email})
          </option>
        ))}
      </select>

      {assignees.length > 0 && (
        <div className="space-y-1.5 mb-3">
          <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#6B7280', fontFamily: 'Manrope' }}>
            WhatsApp numbers (optional, E.164 e.g. +18761234567)
          </div>
          {assignees.map((e) => (
            <div key={e} className="flex items-center gap-2">
              <span className="text-[11px] w-44 truncate" style={{ color: '#374151', fontFamily: 'Manrope' }}>{e}</span>
              <input
                value={whatsappMap[e] || ''}
                onChange={(ev) => setWa(e, ev.target.value)}
                placeholder="+18761234567"
                className="flex-1 text-[12px] rounded-md border px-2 py-1"
                style={{ borderColor: '#D1D5DB', fontFamily: 'Manrope' }}
              />
            </div>
          ))}
          <div className="text-[10px] italic" style={{ color: '#9CA3AF', fontFamily: 'Manrope' }}>
            WhatsApp delivery is currently logged but not sent until Twilio credentials are configured.
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={save}
        disabled={busy}
        className="w-full py-2 rounded-md text-[12px] font-bold disabled:opacity-50"
        style={{ backgroundColor: '#1A2E2E', color: 'white', fontFamily: 'Manrope' }}
        data-testid="pacer-task-save-assignees-btn"
      >
        {busy ? 'Saving…' : 'Save assignees'}
      </button>
    </div>
  );
}

export default function PacerTaskDialog({ open, onClose, payload, allTasks = [] }) {
  const [current, setCurrent] = useState(null);
  useEffect(() => { setCurrent(payload?.task || null); }, [payload]);

  // iter-116 — Hydrate the latest task (with notes/steps/attachments)
  // every time the dialog opens, so collaborators see fresh state.
  useEffect(() => {
    if (!open || !payload || payload.kind === 'tree' || !payload.task?.id) return;
    let cancelled = false;
    axios.get(`${API}/pacer/tasks/${payload.task.id}`)
      .then(({ data }) => { if (!cancelled) setCurrent(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, payload]);

  if (!open || !payload) return null;
  const me = getCurrentUser();
  const canEdit = me && (me.role === 'engineer' || me.role === 'admin');

  // Tree node branch
  if (payload.kind === 'tree') {
    const node = payload.node;
    const rolled = allTasks.filter((t) => (node.task_ids || []).includes(t.id));
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-2xl">
          <div className="h-2 -mt-6 -mx-6 mb-4 rounded-t-lg" style={{ backgroundColor: node.accent_color || '#1F4FBE' }} />
          <DialogHeader>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1" style={{ color: node.accent_color, fontFamily: 'Manrope' }}>
              Master milestone · {fmtDate(node.target_date)}
            </div>
            <DialogTitle className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {node.label}
            </DialogTitle>
            {node.kicker && (
              <DialogDescription className="text-base italic mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {node.kicker}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="mt-3">
            <div className="text-[11px] font-bold tracking-wider uppercase mb-2" style={{ color: '#6B7280', fontFamily: 'Manrope' }}>
              {(node.departments || []).join(' · ')} · {rolled.length} underlying tasks
            </div>
            <ul className="max-h-[40vh] overflow-y-auto space-y-1.5 pr-1">
              {rolled.map((t) => (
                <li key={t.id} className="flex items-start gap-2 text-[12px]" style={{ fontFamily: 'Manrope' }}>
                  <Calendar className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
                  <span className="w-20 flex-shrink-0 text-[10px]" style={{ color: '#6B7280' }}>
                    {fmtDate(t.target_date) || 'TBD'}
                  </span>
                  <span style={{ color: '#1A2E2E' }}>{t.task_name}</span>
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Single task branch
  const task = current || payload.task;
  if (!task) return null;
  const accent = payload.accent || '#1F4FBE';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <div className="h-2 -mt-6 -mx-6 mb-4 rounded-t-lg" style={{ backgroundColor: accent }} />
        <DialogHeader>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1" style={{ color: accent, fontFamily: 'Manrope' }}>
            {task.department}{task.sub_section && task.sub_section !== task.department ? ` · ${task.sub_section}` : ''}
          </div>
          <DialogTitle className="text-2xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {task.task_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2 text-[13px]" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" style={{ color: '#6B7280' }} />
              <span>{fmtDate(task.target_date) || task.target_date_raw || 'No date set'}</span>
            </div>
            <StatusPill status={task.status} />
          </div>
          {task.responsible && (
            <div>
              <span className="font-bold" style={{ color: '#6B7280' }}>Responsible:</span> {task.responsible}
            </div>
          )}
          {task.additional_info && (
            <div className="italic leading-relaxed" style={{ color: '#4A5D5D' }}>
              {task.additional_info}
            </div>
          )}
          {(task.assignees || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center text-[11px]">
              <Mail className="w-3.5 h-3.5" style={{ color: accent }} />
              {(task.assignees || []).map((e) => (
                <span key={e} className="px-2 py-0.5 rounded-md" style={{ backgroundColor: '#EEF2FF', color: '#3730A3' }}>{e}</span>
              ))}
            </div>
          )}
        </div>

        {canEdit && (
          <AdminTaskEditor
            task={task}
            onUpdated={(t) => setCurrent(t)}
          />
        )}

        {/* iter-116 — status / notes / steps / docs available to all signed-in users */}
        <PacerTaskCollab
          task={task}
          accent={accent}
          onChanged={(t) => setCurrent(t)}
        />
      </DialogContent>
    </Dialog>
  );
}
